import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link as MuiLink,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import api from '../api/client';

const ResetPassword = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const newPassword = watch('newPassword', '');

  // Password validation checks
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('invalidResetLink') || 'Invalid reset link');
        setValidating(false);
        return;
      }

      try {
        await api.post('/auth/validate-reset-token', null, {
          params: { token }
        });
        setTokenValid(true);
      } catch (err) {
        console.error('Token validation error:', err);
        setError(err.response?.data?.detail || t('invalidOrExpiredToken') || 'This reset link is invalid or has expired');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: data.newPassword
      });

      setSuccess(t('passwordResetSuccess') || 'Password has been reset successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.detail || t('passwordResetError') || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        p={2}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            backgroundColor: colors.grey[800]
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/forgot-password')}
            sx={{ py: 1.5 }}
          >
            {t('requestNewLink') || 'Request New Reset Link'}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          backgroundColor: colors.grey[800]
        }}
      >
        <Typography variant="h3" fontWeight="bold" mb={3} textAlign="center">
          {t('resetPassword') || 'Reset Password'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('redirectingToLogin') || 'Redirecting to login...'}
            </Typography>
          </Alert>
        )}

        {!success && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label={t('newPassword') || 'New Password'}
              type="password"
              margin="normal"
              {...register('newPassword', {
                required: t('newPasswordRequired') || 'New password is required',
                validate: {
                  minLength: (v) => v.length >= 8 || t('passwordMinLength') || 'Password must be at least 8 characters',
                  hasUppercase: (v) => /[A-Z]/.test(v) || t('passwordNeedsUppercase') || 'Password must contain at least one uppercase letter',
                  hasLowercase: (v) => /[a-z]/.test(v) || t('passwordNeedsLowercase') || 'Password must contain at least one lowercase letter',
                  hasNumber: (v) => /\d/.test(v) || t('passwordNeedsNumber') || 'Password must contain at least one number'
                }
              })}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              disabled={loading}
            />

            <TextField
              fullWidth
              label={t('confirmNewPassword') || 'Confirm New Password'}
              type="password"
              margin="normal"
              {...register('confirmPassword', {
                required: t('confirmPasswordRequired') || 'Please confirm your password',
                validate: (value) =>
                  value === newPassword || t('passwordsDoNotMatch') || 'Passwords do not match'
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              disabled={loading}
            />

            {/* Password requirements checklist */}
            <Box mt={2} mb={2}>
              <Typography variant="body2" color={colors.grey[100]} mb={1}>
                {t('passwordRequirements') || 'Password Requirements'}:
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {hasMinLength ?
                      <CheckCircleIcon color="success" fontSize="small" /> :
                      <CancelIcon color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={t('atLeast8Characters') || 'At least 8 characters'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {hasUppercase ?
                      <CheckCircleIcon color="success" fontSize="small" /> :
                      <CancelIcon color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={t('oneUppercaseLetter') || 'One uppercase letter'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {hasLowercase ?
                      <CheckCircleIcon color="success" fontSize="small" /> :
                      <CancelIcon color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={t('oneLowercaseLetter') || 'One lowercase letter'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {hasNumber ?
                      <CheckCircleIcon color="success" fontSize="small" /> :
                      <CancelIcon color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={t('oneNumber') || 'One number'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? t('resettingPassword') || 'Resetting Password...' : t('resetPassword') || 'Reset Password'}
            </Button>

            <Box textAlign="center">
              <Typography variant="body2" color={colors.grey[100]}>
                {t('rememberPassword') || 'Remember your password?'}{' '}
                <MuiLink
                  component={Link}
                  to="/login"
                  sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
                >
                  {t('login') || 'Login'}
                </MuiLink>
              </Typography>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;
